import { Col, Modal, Row, Space } from "antd";
import React from "react";
import { useNotification } from "../../contexts/Notifications";
import {
  useCreatePlanMutation,
  useDeletePlanMutation,
  useUpdatePlanMutation,
  useUploadPlansTreeMutation,
} from "../../generated/types";
import { Plan, Project } from "../../types";
import { PlanSelector } from "../Selectors/PlanSelector/PlanSelector";
import { PlanCreationModal } from "./PlanCreationModal";
import { PlanDeletionModal, PlanDeletionOptions } from "./PlanDeletionModal";
import { PlanEditModal } from "./PlanEditModal";
import { PlanUploadModal } from "./PlanUploadModal";
import {
  PLAN_CREATED_FAILURE_MESSAGE,
  PLAN_CREATED_SUCCESS_MESSAGE,
  PLAN_DELETED_FAILURE_MESSAGE,
  PLAN_DELETED_SUCCESS_MESSAGE,
  PLAN_UPDATED_FAILURE_MESSAGE,
  PLAN_UPDATED_SUCCESS_MESSAGE,
} from "./messages";
import styles from "./styles.module.css";

type PlansHeaderProps = {
  project: Project;
  title?: string;
  selectedPlan?: Plan;
  onSelect: (plan: Plan | undefined) => void;
  editable?: boolean;
};

// Matches the GraphQL error code thrown by the backend deletePlan resolver.
const SPOTLIGHTS_ATTACHED_CODE = "SPOTLIGHTS_ATTACHED";

export const PlansHeader = (props: PlansHeaderProps) => {
  const { project, title, selectedPlan, onSelect, editable } = props;

  const notify = useNotification();
  // Use the hook form rather than the static Modal.error: antd v5 static
  // methods rely on the legacy render API and silently no-op under React 19.
  const [modalApi, modalContextHolder] = Modal.useModal();
  const [createPlan] = useCreatePlanMutation({
    onCompleted: () => notify(PLAN_CREATED_SUCCESS_MESSAGE, "success"),
    onError: () => notify(PLAN_CREATED_FAILURE_MESSAGE, "error"),
    update: (cache: any) => {
      cache.evict({ fieldName: "plans" });
      cache.gc();
    },
  });
  const [updatePlan] = useUpdatePlanMutation({
    onCompleted: () => notify(PLAN_UPDATED_SUCCESS_MESSAGE, "success"),
    onError: () => notify(PLAN_UPDATED_FAILURE_MESSAGE, "error"),
  });
  const [deletePlan] = useDeletePlanMutation({
    onCompleted: () => notify(PLAN_DELETED_SUCCESS_MESSAGE, "success"),
    onError: (error) => {
      // The backend blocks the delete when spotlights are attached to the
      // affected mappings; surface that as a dialog so the user knows to
      // remove the spotlights first.
      const spotlightError = error.graphQLErrors.find(
        (e) => e.extensions?.code === SPOTLIGHTS_ATTACHED_CODE,
      );
      if (spotlightError) {
        modalApi.error({
          title: "Cannot delete plan",
          content: spotlightError.message,
        });
        return;
      }
      notify(PLAN_DELETED_FAILURE_MESSAGE, "error");
    },
    update: (cache: any, { data }) => {
      if (data?.deletePlan) {
        // deletePlan here holds the id of the deleted plan
        cache.evict({ id: `Plan:${data.deletePlan}` });
        // we need to force a refetch of components without a plan
        // because that set might have changed after deleting a plan
        cache.evict({ fieldName: "componentsExcludingPlan" });
        cache.evict({ fieldName: "components" });
        cache.gc();
      }
    },
  });
  const [uploadFile] = useUploadPlansTreeMutation({
    onCompleted: () => {
      notify("Plans Tree was uploaded successfully", "success");
    },
    onError: (error) =>
      notify(`Failed to upload Plans Tree - ${error}`, "error"),
  });

  const onPlanCreation = async (planName) => {
    const result = await createPlan({
      variables: {
        ...project,
        name: planName,
      },
    });
    if (result.data?.createPlan) {
      onSelect(result.data?.createPlan);
    }
  };
  const onPlanUpload = async (planId: string, file: File) => {
    await uploadFile({
      variables: {
        ...project,
        file: file,
        planId: planId,
      },
    });
  };
  const onSubmitUpdate = async (updatedPlan: Plan) => {
    await updatePlan({
      variables: {
        ...project,
        planId: updatedPlan.id,
        name: updatedPlan.name,
        startDate: updatedPlan.startDate,
      },
    });
    onSelect(updatedPlan);
  };
  const onPlanDeletion = async (
    planId: string,
    options: PlanDeletionOptions,
  ) => {
    try {
      const result = await deletePlan({
        variables: {
          ...project,
          planId: planId,
          deleteComponents: options.deleteComponents,
          deleteMappings: options.deleteMappings,
        },
      });
      const deleteSuccessful = result.data?.deletePlan;
      if (deleteSuccessful) {
        onSelect(undefined);
      }
    } catch {
      // Errors are surfaced by the mutation's onError handler.
    }
  };

  return (
    // modalContextHolder must sit outside <Row>: antd Row is a flex container
    // that expects Col children and warns on any other direct child.
    <>
      {modalContextHolder}
      <Row>
        <Col style={{ position: "relative", width: "100%" }}>
          {title && <h2>{title}</h2>}
          {editable && (
            <Space style={{ position: "absolute", right: "12px", top: "0" }}>
              {selectedPlan && (
                <>
                  <PlanEditModal
                    plan={selectedPlan}
                    onSubmitUpdate={onSubmitUpdate}
                  />
                  <PlanDeletionModal
                    onDelete={(options) =>
                      onPlanDeletion(selectedPlan.id, options)
                    }
                  />
                </>
              )}
              <PlanCreationModal onCreate={onPlanCreation} />
              <PlanUploadModal onUpload={onPlanUpload} project={project} />
            </Space>
          )}
        </Col>
        <Col className={styles["selector-container"]}>
          <PlanSelector
            project={project}
            selectedId={selectedPlan?.id}
            onSelect={onSelect}
          />
        </Col>
      </Row>
    </>
  );
};

export default PlansHeader;
